import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import withStyles from 'isomorphic-style-loader/withStyles';
import { compose } from 'redux';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import getWindow from '../../../../helpers/windowProvider';
import {
  SHOP_PATH,
  RETAIL_PRICE,
  PRODUCT_TYPE,
  SALES_CHANNELS,
} from '../../../../constants';
import Link from '../../../Link';
import Card from '../../../Card';
import Tile from '../Tile/Tile';
import useScrollStopDetector from '../../../../hooks/useScrollStopDetector';
import ProductInfoModal from '../../../ProductInfoModal/ProductInfoModal';

import s from './ProductList.scss';

const ProductList = ({
  products,
  isDesktop,
  onProductClick,
  currencyCode,
  originalPresentations,
  salesChannelId,
  locale,
  openProductInModal,
  noTiles,
}) => {
  const queryParams = getWindow().location.search;
  const [tilesConfig, setTilesConfig] = useState({});
  useScrollStopDetector(() => {
    const topLimit = 160;
    const bottomLimit =
      (window.innerHeight || document.documentElement.clientHeight) - 70;

    function isInView(element) {
      const boundingClientRect = element.getBoundingClientRect();
      return (
        boundingClientRect.top >= topLimit &&
        boundingClientRect.bottom <= bottomLimit
      );
    }

    const impressions = [];
    document.querySelectorAll('.impressionable-product').forEach(element => {
      if (isInView(element)) {
        const index = products.findIndex(product => product.id === element.id);
        const { id, productTitle: name, price } = products[index];
        impressions.push({
          id,
          name,
          price,
          position: index + 1,
        });
      }
    });
    const event = {
      event: 'productImpression',
      currencyCode,
      impressions,
    };

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push(event);
  });

  function filterInvEntries(deliveryMet) {
    let invForReturn;
    deliveryMet.forEach(t => {
      invForReturn = [
        ...new Map(
          t.inventoryEntries.map(item => [item.defaultPriceCurrency, item]),
        ).values(),
      ];
    });
    return invForReturn;
  }

  function filterYourPrice(delMethods) {
    return filterInvEntries(delMethods)?.reduce?.((retVal, t) => {
      if (t.defaultPriceCurrency === currencyCode) {
        return t.price;
      }
      return retVal;
    }, 0);
  }

  function filterRetailPrice(delMethods) {
    const invEntries = filterInvEntries(delMethods);
    const forRetalPrice = invEntries?.find?.(inv => {
      return inv.defaultPriceCurrency === currencyCode;
    });
    let filterRetail;
    if (!isEmpty(forRetalPrice)) {
      filterRetail = forRetalPrice.attributes.reduce((r, ret) => {
        if (ret.name === RETAIL_PRICE) {
          return ret.value;
        }
        return r;
      }, false);
    }
    const filterRetailObj = { name: RETAIL_PRICE, value: filterRetail };
    return filterRetailObj;
  }
  const filterTilesConfig = () => {
    const originalPresentationsValues = Object.values(originalPresentations);
    const filteredConfig = originalPresentationsValues.reduce(
      (
        retVal,
        { languageCode, inSalesChannel, presentationKey, image, textContent },
      ) => {
        if (!inSalesChannel && retVal[presentationKey]) {
          return retVal;
        }
        if (
          (!languageCode || locale === languageCode?.toLowerCase()) &&
          presentationKey &&
          (!inSalesChannel || inSalesChannel === salesChannelId)
        ) {
          // eslint-disable-next-line no-param-reassign
          retVal[presentationKey] = image || textContent;
        }
        return retVal;
      },
      {},
    );
    setTilesConfig(filteredConfig);
  };

  useEffect(() => {
    if (noTiles) return;
    filterTilesConfig();
  }, [salesChannelId, originalPresentations]);
  return (
    <ol
      className={cx(
        s.productList,
        salesChannelId === SALES_CHANNELS.REDEEM
          ? s.redeemProductListMargin
          : s.regularProductListMargin,
      )}
    >
      {!queryParams && (
        <>
          {tilesConfig?.showTileOne === 'true' && (
            <Tile
              className={s.tileOne}
              title={tilesConfig.tileOneTitle}
              text={tilesConfig.tileOneText}
              link={tilesConfig.tileOneLink}
              image={tilesConfig.tileOneImage}
              buttonText={tilesConfig.tileOneButtonText}
            />
          )}
          {products.length > 4 && tilesConfig?.showTileTwo === 'true' && (
            <Tile
              className={s.tileTwo}
              title={tilesConfig.tileTwoTitle}
              text={tilesConfig.tileTwoText}
              link={tilesConfig.tileTwoLink}
              image={tilesConfig.tileTwoImage}
              buttonText={tilesConfig.tileTwoButtonText}
            />
          )}
          {products.length > 8 && tilesConfig?.showTileThree === 'true' && (
            <Tile
              className={s.tileThree}
              title={tilesConfig.tileThreeTitle}
              text={tilesConfig.tileThreeText}
              link={tilesConfig.tileThreeLink}
              image={tilesConfig.tileThreeImage}
              buttonText={tilesConfig.tileThreeButtonText}
            />
          )}
          {products.length > 12 && tilesConfig?.showTileFour === 'true' && (
            <Tile
              className={s.tileFour}
              title={tilesConfig.tileFourTitle}
              text={tilesConfig.tileFourText}
              link={tilesConfig.tileFourLink}
              image={tilesConfig.tileFourImage}
              buttonText={tilesConfig.tileFourButtonText}
            />
          )}
        </>
      )}
      {products.map(
        (
          {
            id,
            productTitle,
            productType,
            productShortDescription,
            deliveryMethods,
            retailPrice,
            price,
            productLogo,
            productSlug,
            productCampaign,
          },
          index,
        ) => (
          <li key={id} className="impressionable-product" id={id}>
            <Card
              productTitle={productTitle}
              productType={productType}
              productCampaign={productCampaign}
              productShortDescription={productShortDescription}
              retailPrice={
                productType === PRODUCT_TYPE.physicalProduct
                  ? filterRetailPrice(deliveryMethods)
                  : retailPrice
              }
              price={
                productType === PRODUCT_TYPE.physicalProduct
                  ? filterYourPrice(deliveryMethods)
                  : price
              }
              hoverType={isDesktop ? 'box-shadow' : ''}
              productLogo={
                productLogo && productLogo[0]?.thumbnail
                  ? productLogo[0].thumbnail
                  : null
              }
              link={
                openProductInModal ? (
                  <ProductInfoModal
                    productId={id}
                    hoverType={isDesktop ? 'box-shadow' : ''}
                  />
                ) : (
                  <Link
                    to={`${SHOP_PATH}/${productSlug}/${id}`}
                    onClick={() =>
                      onProductClick(id, productTitle, price, index)
                    }
                  />
                )
              }
            />
          </li>
        ),
      )}
    </ol>
  );
};

ProductList.propTypes = {
  products: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      productTitle: PropTypes.string.isRequired,
      price: PropTypes.number.isRequired,
    }).isRequired,
  ).isRequired,
  isDesktop: PropTypes.bool.isRequired,
  onProductClick: PropTypes.func.isRequired,
  currencyCode: PropTypes.string.isRequired,
  originalPresentations: PropTypes.shape().isRequired,
  salesChannelId: PropTypes.string.isRequired,
  locale: PropTypes.string.isRequired,
  openProductInModal: PropTypes.bool,
  noTiles: PropTypes.bool,
};

ProductList.defaultProps = {
  openProductInModal: false,
  noTiles: false,
};

const mapState = state => ({
  currencyCode: state.intl.currency,
  locale: state.intl.locale,
  originalPresentations: state?.config?.presentations?.originalPresentations,
  salesChannelId: state.config.salesChannelId,
});

export default compose(withStyles(s), connect(mapState))(ProductList);
